import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import TeamModal from 'src/components/TeamModal'
import { useTeamForEdit } from '../hooks/useTeamForEdit'

interface EditTeamModalProps {
  teamId: string
  orgId: string
  onClose: () => void
  onSaved: () => void
}

/**
 * Edit dialog for a team selected in the operations tree.
 *
 * Its own component, not an inline branch of `EditEntityLauncher`, so the fetch
 * hook below is never called conditionally.
 *
 * `TeamModal` takes the WHOLE team as `initialState` and never fetches — it
 * seeds its form from whatever it is handed, then PUTs the result back. Seeding
 * it from a tree node (id + name + sport) therefore blanked every field the
 * tree does not carry: the logo showed "Click to Upload" on a team that had
 * one, and the contact fields and visibility flag were written back empty. So
 * the real team is loaded first and the modal is held until it arrives.
 */
const EditTeamModal = ({
  teamId,
  orgId,
  onClose,
  onSaved,
}: EditTeamModalProps) => {
  'use memo'

  const queryClient = useQueryClient()
  const { team } = useTeamForEdit(teamId)

  // This team's own cache entry, not just the tree: `onSaved` refetches the
  // tree (which feeds the rail and the grid), but the modal seeds itself from
  // THIS query — so without invalidating it, reopening the modal would show
  // the pre-edit values served from cache.
  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.team.byId(teamId) })
    onSaved()
  }

  // Mounting with a partial team would let INIT_EDIT_MODE seed the form from
  // it, and the modal re-inits whenever `initialState` changes identity — so a
  // half-filled form now would be overwritten under the user a moment later.
  if (!team) {
    return null
  }

  return (
    <TeamModal
      isOpen
      onClose={onClose}
      initialState={team}
      organizationId={orgId}
      // Deletion lives on the tree row and the grid's row menu in this tab,
      // so the modal's Danger Zone would be a third, redundant path.
      permission={{ delete: false }}
      onSuccess={handleSuccess}
    />
  )
}

export default EditTeamModal
